const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour24 = Math.floor(index / 2);
  const minute = index % 2 === 0 ? 0 : 30;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  const minuteText = String(minute).padStart(2, '0');
  return {
    value: `${String(hour24).padStart(2, '0')}:${minuteText}`,
    label: `${hour12}:${minuteText} ${period}`,
  };
});

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '3px solid #ffffff',
  borderRadius: 10,
  background: '#ffffff',
  color: '#000000',
  fontWeight: 900,
  boxSizing: 'border-box',
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  minWidth: 0,
};

const labelStyle = {
  fontWeight: 900,
  textTransform: 'uppercase',
  fontSize: 12,
};

const warningMessage = (warning) => {
  switch (warning.code) {
    case 'BUSINESS_CLOSED':
      return 'Business is closed on this date.';
    case 'OUTSIDE_BUSINESS_HOURS':
      return 'This appointment is outside business hours.';
    case 'BUSINESS_HOURS_UNKNOWN':
      return 'Business hours are unavailable for this date.';
    case 'EMPLOYEE_OVERLAP_EXISTING':
      return 'The assigned employee conflicts with an existing appointment.';
    case 'EMPLOYEE_OVERLAP_BATCH': {
      const sequences = Array.isArray(warning.conflicting_sequences)
        ? warning.conflicting_sequences.map((sequence) => `#${sequence}`).join(', ')
        : '';
      return sequences
        ? `The assigned employee conflicts with recurring occurrence ${sequences}.`
        : 'The assigned employee conflicts with another recurring occurrence.';
    }
    default:
      return String(warning.code || 'Scheduling warning').replace(/_/g, ' ');
  }
};

const employeeName = (employees, employeeId) => {
  if (employeeId === null || employeeId === undefined || employeeId === '') {
    return 'Unassigned';
  }
  const employee = employees.find((item) => String(item.id) === String(employeeId));
  return String(employee?.name || employee?.full_name || '').trim()
    || `Employee ${employeeId}`;
};

const updateOccurrenceOverride = ({
  occurrence,
  field,
  value,
  setOccurrenceOverrides,
}) => {
  setOccurrenceOverrides((currentOverrides) => {
    const overrides = Array.isArray(currentOverrides) ? currentOverrides : [];
    const index = overrides.findIndex((override) => (
      override.recurrence_sequence === occurrence.recurrence_sequence
    ));
    const nextOverride = {
      ...(index >= 0 ? overrides[index] : {}),
      recurrence_sequence: occurrence.recurrence_sequence,
      [field]: value,
    };

    if (index < 0) return [...overrides, nextOverride];
    return overrides.map((override, overrideIndex) => (
      overrideIndex === index ? nextOverride : override
    ));
  });
};

const RecurringTimeSelect = ({ id, label, value, onChange }) => (
  <label htmlFor={id} style={fieldStyle}>
    <span style={labelStyle}>{label}</span>
    <select id={id} style={inputStyle} value={value || ''} onChange={onChange}>
      <option value="">-- SELECT TIME --</option>
      {TIME_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const RecurringOccurrencePreview = ({
  previewResult,
  previewLoading = false,
  employees = [],
  setOccurrenceOverrides,
}) => {
  if (previewLoading) {
    return (
      <section aria-live="polite" style={{ fontWeight: 900, padding: 12 }}>
        Generating authoritative appointment preview…
      </section>
    );
  }

  const occurrences = Array.isArray(previewResult?.occurrences)
    ? previewResult.occurrences
    : [];
  const warnings = Array.isArray(previewResult?.warnings)
    ? previewResult.warnings
    : [];

  if (!occurrences.length) {
    return (
      <section
        aria-live="polite"
        style={{
          border: '3px solid #000000',
          borderRadius: 12,
          background: '#E8761D',
          color: '#000000',
          padding: '14px 16px',
          fontWeight: 900,
          textAlign: 'center',
        }}
      >
        Select REVIEW APPOINTMENTS &amp; WARNINGS to generate the authoritative preview required for creation.
      </section>
    );
  }

  return (
    <section
      aria-label="Recurring appointment occurrence preview"
      style={{
        border: '3px solid #E8761D',
        borderRadius: 14,
        background: '#000000',
        color: '#ffffff',
        padding: 12,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          color: '#E8C98F',
          fontWeight: 900,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Appointment Preview — {occurrences.length} Occurrence{occurrences.length === 1 ? '' : 's'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {occurrences.map((occurrence) => {
          const sequence = occurrence.recurrence_sequence;
          const occurrenceWarnings = warnings.filter((warning) => (
            warning.recurrence_sequence === sequence
          ));

          return (
            <article
              key={sequence}
              style={{
                border: '3px solid #E8E2DA',
                borderRadius: 12,
                background: '#365C91',
                padding: 12,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 10,
                }}
              >
                <strong style={{ color: '#E8C98F', textTransform: 'uppercase' }}>
                  Occurrence #{sequence}
                </strong>
                <span style={{ fontWeight: 900 }}>
                  Assigned Employee: {employeeName(employees, occurrence.employee_id)}
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 10,
                }}
              >
                <label htmlFor={`recurring-occurrence-date-${sequence}`} style={fieldStyle}>
                  <span style={labelStyle}>Date</span>
                  <input
                    id={`recurring-occurrence-date-${sequence}`}
                    type="date"
                    style={inputStyle}
                    value={occurrence.date || ''}
                    onChange={(event) => updateOccurrenceOverride({
                      occurrence,
                      field: 'date',
                      value: event.target.value,
                      setOccurrenceOverrides,
                    })}
                  />
                </label>

                <RecurringTimeSelect
                  id={`recurring-occurrence-start-${sequence}`}
                  label="Start Time"
                  value={occurrence.start_time}
                  onChange={(event) => updateOccurrenceOverride({
                    occurrence,
                    field: 'start_time',
                    value: event.target.value,
                    setOccurrenceOverrides,
                  })}
                />

                <RecurringTimeSelect
                  id={`recurring-occurrence-end-${sequence}`}
                  label="End Time"
                  value={occurrence.end_time}
                  onChange={(event) => updateOccurrenceOverride({
                    occurrence,
                    field: 'end_time',
                    value: event.target.value,
                    setOccurrenceOverrides,
                  })}
                />

                <label htmlFor={`recurring-occurrence-employee-${sequence}`} style={fieldStyle}>
                  <span style={labelStyle}>Employee</span>
                  <select
                    id={`recurring-occurrence-employee-${sequence}`}
                    style={inputStyle}
                    value={occurrence.employee_id ?? ''}
                    onChange={(event) => updateOccurrenceOverride({
                      occurrence,
                      field: 'employee_id',
                      value: event.target.value ? Number(event.target.value) : null,
                      setOccurrenceOverrides,
                    })}
                  >
                    <option value="">-- UNASSIGNED --</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {String(employee.name || employee.full_name || '').trim()
                          || `Employee ${employee.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {occurrenceWarnings.length ? (
                <div
                  role="status"
                  style={{
                    marginTop: 10,
                    border: '3px solid #E8761D',
                    borderRadius: 10,
                    background: '#fff3cd',
                    color: '#000000',
                    padding: 10,
                  }}
                >
                  <div style={{ fontWeight: 900, textTransform: 'uppercase' }}>
                    Scheduling Warnings
                  </div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontWeight: 800 }}>
                    {occurrenceWarnings.map((warning, warningIndex) => (
                      <li key={`${warning.code}-${warningIndex}`}>
                        {warningMessage(warning)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 12,
          color: '#E8C98F',
          fontWeight: 900,
          textAlign: 'center',
        }}
      >
        Changing an occurrence invalidates this preview. Select PREVIEW APPOINTMENTS again before creating.
      </div>
    </section>
  );
};

export default RecurringOccurrencePreview;
